import React, { useState, useEffect, useCallback } from "react";
import SEO from "./components/SEO";

const HEADER_HEIGHT = 64; // pixels reserved for the app's main header
const CONFIG_STORAGE_KEY = "gameSlidesConfig_v1";

const THEMES = [
  {
    id: "black",
    name: "Pure Black",
    background: "#000000",
    color: "#ffffff",
  },
  {
    id: "dark-gradient",
    name: "Dark Gradient",
    background: "linear-gradient(135deg, #000000, #333333)",
    color: "#ffffff",
  },
  {
    id: "sunset",
    name: "Sunset",
    background: "linear-gradient(135deg, #ff7e5f, #feb47b)",
    color: "#ffffff",
  },
  {
    id: "ocean",
    name: "Ocean",
    background: "linear-gradient(135deg, #00c6ff, #0072ff)",
    color: "#ffffff",
  },
  {
    id: "forest",
    name: "Forest",
    background: "linear-gradient(135deg, #0f9b0f, #000000)",
    color: "#ffffff",
  },
  {
    id: "neon",
    name: "Neon Glow",
    background: "radial-gradient(circle at top, #ff00ff, #000000)",
    color: "#ffffff",
  },
  {
    id: "warm",
    name: "Warm Room",
    background: "linear-gradient(135deg, #4a148c, #ff6f00)",
    color: "#ffffff",
  },
];

function parseParagraphs(raw) {
  if (!raw) return [];
  return raw
    .split(/\n{2,}|\r\n{2,}/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
}

function formatTime(seconds) {
  const s = Math.max(0, Math.floor(seconds));
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
}

export default function GameSlidesPage() {
  const [paragraphs, setParagraphs] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  // Right panel open/close
  const [isPanelOpen, setIsPanelOpen] = useState(true);

  // Bottom scoreboard panel open/close
  const [isBottomOpen, setIsBottomOpen] = useState(true);

  // Index section collapse + search
  const [isIndexOpen, setIsIndexOpen] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // Input modes
  const [inputMode, setInputMode] = useState("text"); // "text" | "url" | "file"
  const [textInput, setTextInput] = useState("");
  const [urlInput, setUrlInput] = useState("");
  const [fileName, setFileName] = useState("");

  // Display settings
  const [selectedThemeId, setSelectedThemeId] = useState("black");
  const [fontSize, setFontSize] = useState(40);
  const [lineHeight, setLineHeight] = useState(1.4);
  const [textAlign, setTextAlign] = useState("center");
  const [verticalAlign, setVerticalAlign] = useState("center"); 

  const [showSlideNumbers, setShowSlideNumbers] = useState(true);
  const [lockFontSize, setLockFontSize] = useState(false);
  const [fadeTransition, setFadeTransition] = useState(true);

  // Layout / content style
  const [layoutMode, setLayoutMode] = useState("auto"); // "auto" | "paragraph" | "bullets" | "tiles" | "table"

  // Timer state (per game)
  const [timerDuration, setTimerDuration] = useState(60); // seconds
  const [remainingTime, setRemainingTime] = useState(60);
  const [isTimerRunning, setIsTimerRunning] = useState(false);

  // Scoreboard
  const [teams, setTeams] = useState([
    { id: 1, name: "Team 1", score: 0 },
    { id: 2, name: "Team 2", score: 0 },
    { id: 3, name: "Team 3", score: 0 },
    { id: 4, name: "Team 4", score: 0 },
  ]);

  // Track which games are "played"
  const [playedMap, setPlayedMap] = useState({});

  // Show config JSON
  const [showConfigJson, setShowConfigJson] = useState(false);

  const currentTheme =
    THEMES.find((t) => t.id === selectedThemeId) || THEMES[0];

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => {
      if (paragraphs.length === 0) return 0;
      const next = Math.min(prev + 1, paragraphs.length - 1);
      return next;
    });
  }, [paragraphs.length]);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => {
      if (paragraphs.length === 0) return 0;
      const next = Math.max(prev - 1, 0);
      return next;
    });
  }, [paragraphs.length]);

  const shuffleGames = () => {
    setParagraphs((prev) => {
      const arr = [...prev];
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr;
    });
    setCurrentIndex(0);
    setPlayedMap({});
  };

  // Keyboard navigation (left/right)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [goNext, goPrev]);

  // Timer tick
  useEffect(() => {
    if (!isTimerRunning) return;
    if (remainingTime <= 0) {
      setIsTimerRunning(false);
      return;
    }
    const id = setInterval(() => {
      setRemainingTime((prev) => {
        if (prev <= 1) {
          clearInterval(id);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [isTimerRunning, remainingTime]);

  // Reset timer when slide changes or duration changes
  useEffect(() => {
    setIsTimerRunning(false);
    setRemainingTime(timerDuration);
  }, [currentIndex, timerDuration]);

  const handleTimerStartPause = () => {
    if (remainingTime <= 0) {
      setRemainingTime(timerDuration);
      setIsTimerRunning(true);
    } else {
      setIsTimerRunning((running) => !running);
    }
  };

  const handleTimerReset = () => {
    setIsTimerRunning(false);
    setRemainingTime(timerDuration);
  };

  // Loading helpers
  const loadFromText = () => {
    const parsed = parseParagraphs(textInput);
    if (parsed.length > 0) {
      setParagraphs(parsed);
      setCurrentIndex(0);
      setPlayedMap({});
    } else {
      alert("No paragraphs found. Please add some text first.");
    }
  };

  const loadFromUrl = async () => {
    try {
      if (!urlInput) {
        alert("Please enter a URL first.");
        return;
      }
      const res = await fetch(urlInput);
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      const text = await res.text();
      const parsed = parseParagraphs(text);
      if (parsed.length === 0) {
        alert("Could not find paragraphs in the fetched content.");
        return;
      }
      setParagraphs(parsed);
      setCurrentIndex(0);
      setPlayedMap({});
    } catch (err) {
      console.error(err);
      alert(
        "There was an issue loading from that URL (maybe CORS or a non-text page)."
      );
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);

    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = String(ev.target?.result || "");
      const parsed = parseParagraphs(text);
      if (parsed.length === 0) {
        alert("Could not find paragraphs in that file. Make sure it is text.");
        return;
      }
      setParagraphs(parsed);
      setCurrentIndex(0);
      setPlayedMap({});
    };
    reader.readAsText(file);
  };

  const slideText =
    paragraphs[currentIndex] || "Add game descriptions and press Load!";

  // Auto-detect layout type if layoutMode === "auto"
  const detectLayoutMode = (text) => {
    const lines = text
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    if (lines.length === 0) return "paragraph";
    if (lines.length === 1) return "paragraph";

    const bulletRegex = /^[-*•]\s+/;
    if (lines.every((l) => bulletRegex.test(l))) {
      return "bullets";
    }

    if (text.includes("|")) {
      const rows = lines.map((l) => l.split("|").length);
      const distinctCounts = new Set(rows);
      if (distinctCounts.size === 1 && [...distinctCounts][0] > 1) {
        return "table";
      }
    }

    return "tiles";
  };

  const effectiveLayoutMode =
    layoutMode === "auto" ? detectLayoutMode(slideText) : layoutMode;

  const renderSlideContent = () => {
    const baseText = slideText;
    if (!baseText) return null;

    if (effectiveLayoutMode === "paragraph") {
      return <div>{baseText}</div>;
    }

    const lines = baseText
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter((l) => l.length > 0);

    if (effectiveLayoutMode === "bullets") {
      const cleaned = lines.map((l, idx) =>
        l.replace(/^[-*•]\s+/, "").trim() || `Item ${idx + 1}`
      );
      return (
        <ul style={styles.bulletList}>
          {cleaned.map((item, i) => (
            <li key={i} style={styles.bulletItem}>
              {item}
            </li>
          ))}
        </ul>
      );
    }

    if (effectiveLayoutMode === "tiles") {
      return (
        <div style={styles.tilesGrid}>
          {lines.map((l, i) => (
            <div key={i} style={styles.tile}>
              {l}
            </div>
          ))}
        </div>
      );
    }

    if (effectiveLayoutMode === "table") {
      const rows = lines.map((l) =>
        l
          .split("|")
          .map((cell) => cell.trim())
          .filter((cell) => cell.length > 0)
      );
      if (rows.length === 0) return <div>{baseText}</div>;
      const [header, ...body] = rows;
      return (
        <table style={styles.table}>
          <thead>
            <tr>
              {header.map((h, i) => (
                <th key={i} style={styles.tableHeaderCell}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {body.map((row, r) => (
              <tr key={r}>
                {row.map((cell, c) => (
                  <td key={c} style={styles.tableCell}>
                    {cell}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    }

    return <div>{baseText}</div>;
  };

  // Search + index data
  const filteredIndices = paragraphs
    .map((p, idx) => ({ text: p, index: idx }))
    .filter(({ text }) => {
      if (!searchQuery.trim()) return true;
      return text.toLowerCase().includes(searchQuery.toLowerCase());
    });

  const handleJumpToIndex = (idx) => {
    setCurrentIndex(idx);
  };

  const togglePlayedForCurrent = () => {
    setPlayedMap((prev) => {
      const copy = { ...prev };
      if (copy[currentIndex]) {
        delete copy[currentIndex];
      } else {
        copy[currentIndex] = true;
      }
      return copy;
    });
  };

  // Scoreboard handlers
  const updateTeamName = (id, name) => {
    setTeams((prev) =>
      prev.map((t) => (t.id === id ? { ...t, name } : t))
    );
  };

  const adjustTeamScore = (id, delta) => {
    setTeams((prev) =>
      prev.map((t) =>
        t.id === id ? { ...t, score: Math.max(0, t.score + delta) } : t
      )
    );
  };

  const resetScores = () => {
    setTeams((prev) => prev.map((t) => ({ ...t, score: 0 })));
  };

  const addTeam = () => {
    setTeams((prev) => {
      const nextNumber = prev.length + 1;
      return [
        ...prev,
        {
          id: Date.now(),
          name: `Team ${nextNumber}`,
          score: 0,
        },
      ];
    });
  };

  const removeTeam = () => {
    setTeams((prev) => {
      if (prev.length <= 1) return prev;
      return prev.slice(0, -1);
    });
  };

  // Build / apply config for JSON + autosave
  const buildConfig = () => ({
    paragraphs,
    currentIndex,
    selectedThemeId,
    fontSize,
    lineHeight,
    textAlign,
    showSlideNumbers,
    lockFontSize,
    fadeTransition,
    layoutMode,
    timerDuration,
    remainingTime,
    teams,
    playedMap,
    searchQuery,
  });

  const applyConfig = (cfg) => {
    if (!cfg || typeof cfg !== "object") return;
    if (Array.isArray(cfg.paragraphs)) setParagraphs(cfg.paragraphs);
    if (typeof cfg.currentIndex === "number") {
      const maxIndex =
        Array.isArray(cfg.paragraphs) && cfg.paragraphs.length > 0
          ? cfg.paragraphs.length - 1
          : 0;
      setCurrentIndex(Math.min(Math.max(0, cfg.currentIndex), maxIndex));
    }
    if (cfg.selectedThemeId) setSelectedThemeId(cfg.selectedThemeId);
    if (typeof cfg.fontSize === "number") setFontSize(cfg.fontSize);
    if (typeof cfg.lineHeight === "number") setLineHeight(cfg.lineHeight);
    if (typeof cfg.textAlign === "string") setTextAlign(cfg.textAlign);
    if (typeof cfg.showSlideNumbers === "boolean")
      setShowSlideNumbers(cfg.showSlideNumbers);
    if (typeof cfg.lockFontSize === "boolean")
      setLockFontSize(cfg.lockFontSize);
    if (typeof cfg.fadeTransition === "boolean")
      setFadeTransition(cfg.fadeTransition);
    if (typeof cfg.layoutMode === "string") setLayoutMode(cfg.layoutMode);
    if (typeof cfg.timerDuration === "number")
      setTimerDuration(cfg.timerDuration);
    if (typeof cfg.remainingTime === "number")
      setRemainingTime(cfg.remainingTime);
    if (Array.isArray(cfg.teams)) setTeams(cfg.teams);
    if (cfg.playedMap && typeof cfg.playedMap === "object")
      setPlayedMap(cfg.playedMap);
    if (typeof cfg.searchQuery === "string") setSearchQuery(cfg.searchQuery);
    setIsTimerRunning(false);
  };

  // Load config from localStorage on mount
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const raw = window.localStorage.getItem(CONFIG_STORAGE_KEY);
      if (!raw) return;
      const cfg = JSON.parse(raw);
      applyConfig(cfg);
    } catch (e) {
      console.error("Failed to load config", e);
    }
  }, []);

  // Auto-save config to localStorage on every relevant change
  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const cfg = buildConfig();
      window.localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(cfg));
    } catch (e) {
      console.error("Failed to save config", e);
    }
  }, [
    paragraphs,
    currentIndex,
    selectedThemeId,
    fontSize,
    lineHeight,
    textAlign,
    showSlideNumbers,
    lockFontSize,
    fadeTransition,
    layoutMode,
    timerDuration,
    remainingTime,
    teams,
    playedMap,
    searchQuery,
  ]);

  const configJson = JSON.stringify(buildConfig(), null, 2);

  const handleConfigFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = String(ev.target?.result || "");
        const parsed = JSON.parse(text);
        applyConfig(parsed);
      } catch (err) {
        console.error(err);
        alert("Invalid config JSON file.");
      }
    };
    reader.readAsText(file);
  };

  const handleDownloadConfig = () => {
    try {
      const blob = new Blob([configJson], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "game-config.json";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      alert("Could not download config.");
    }
  };

  // Simple visual reports
  const totalGames = paragraphs.length;
  const playedCount = Object.keys(playedMap).length;
  const completionPercent =
    totalGames === 0 ? 0 : Math.round((playedCount / totalGames) * 100);
  const maxScore = teams.reduce(
    (max, t) => (t.score > max ? t.score : max),
    0
  );
  const sortedTeams = [...teams].sort((a, b) => b.score - a.score);

  return (
    <div style={styles.appRoot}>
      <SEO
        title="Games"
        description="Interactive slides and group games for gatherings."
      />


        
      <style>
        {`
          .slide-content {
            transition: opacity 0.4s ease;
          }
        `}
      </style>

      

      {/* Floating right panel toggle */}
      <button
        style={styles.panelToggleButton}
        onClick={() => setIsPanelOpen((open) => !open)}
      >
        {isPanelOpen ? "✕ " : "☰ "}
      </button>


      

      {/* Right overlay panel (planning / advanced controls) */}
      <div
        style={{
          ...styles.sidePanel,
          transform: isPanelOpen ? "translateX(0)" : "translateX(100%)",
        }}
      >


        <div style={styles.sidePanelHeader}>
          <span>Games &amp; Controls</span>
        </div>

        <div style={styles.sidePanelBody}>
          {/* === INDEX / SEARCH SECTION === */}
          <div style={styles.section}>
            <div
              style={styles.sectionTitleRow}
              onClick={() => setIsIndexOpen((open) => !open)}
            >
              <span style={styles.sectionTitle}>Index &amp; search</span>
              <span style={styles.sectionToggleIcon}>
                {isIndexOpen ? "▼" : "▲"}
              </span>
            </div>
            {isIndexOpen && (
              <>
                <input
                  style={styles.textInput}
                  placeholder="Search game..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                <div style={styles.indexSummary}>
                  {filteredIndices.length} / {paragraphs.length} games
                </div>

                {/* Slider to jump to game */}
                {paragraphs.length > 0 && (
                  <>
                    <label style={styles.label}>
                      Jump to game (1 - {paragraphs.length})
                    </label>
                    <input
                      type="range"
                      min={0}
                      max={Math.max(0, paragraphs.length - 1)}
                      value={currentIndex}
                      onChange={(e) =>
                        handleJumpToIndex(parseInt(e.target.value, 10))
                      }
                      style={styles.range}
                    />
                  </>
                )}

                <div style={styles.indexList}>
                  {filteredIndices.map(({ text, index }) => {
                    const firstLine =
                      text.split(/\r?\n/)[0]?.slice(0, 60) || "Game";
                    const isCurrent = index === currentIndex;
                    const isPlayed = !!playedMap[index];
                    return (
                      <button
                        key={index}
                        style={{
                          ...styles.indexItem,
                          ...(isCurrent ? styles.indexItemActive : {}),
                        }}
                        onClick={() => handleJumpToIndex(index)}
                      >
                        <div style={styles.indexItemHeader}>
                          <span>Game {index + 1}</span>
                          {isPlayed && (
                            <span style={styles.playedDot}>● played</span>
                          )}
                        </div>
                        <div style={styles.indexItemText}>{firstLine}</div>
                      </button>
                    );
                  })}
                  {filteredIndices.length === 0 && (
                    <div style={styles.emptyIndex}>
                      No games match your search.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* === GAME INPUT SECTION === */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Game input</div>

            <div style={styles.tabRow}>
              <button
                style={{
                  ...styles.tabButton,
                  ...(inputMode === "text" ? styles.tabButtonActive : {}),
                }}
                onClick={() => setInputMode("text")}
              >
                Text
              </button>
              <button
                style={{
                  ...styles.tabButton,
                  ...(inputMode === "url" ? styles.tabButtonActive : {}),
                }}
                onClick={() => setInputMode("url")}
              >
                From URL
              </button>
              <button
                style={{
                  ...styles.tabButton,
                  ...(inputMode === "file" ? styles.tabButtonActive : {}),
                }}
                onClick={() => setInputMode("file")}
              >
                Upload file
              </button>
            </div>

            {inputMode === "text" && (
              <>
                <label style={styles.label}>
                  Paste your game paragraphs here (blank line between games):
                </label>
                <textarea
                  style={styles.textarea}
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  placeholder={`Game 1: Everyone stand up and...

Game 2: Form a circle and...`}
                />
                <button style={styles.primaryButton} onClick={loadFromText}>
                  Load games from text
                </button>
              </>
            )}

            {inputMode === "url" && (
              <>
                <label style={styles.label}>URL with paragraphs:</label>
                <input
                  type="url"
                  style={styles.textInput}
                  value={urlInput}
                  onChange={(e) => setUrlInput(e.target.value)}
                  placeholder="https://example.com/games.txt"
                />
                <button style={styles.primaryButton} onClick={loadFromUrl}>
                  Load games from URL
                </button>
                <p style={styles.helpText}>
                  Works best with plain text / markdown pages. Some sites may
                  block cross-origin requests.
                </p>
              </>
            )}

            {inputMode === "file" && (
              <>
                <label style={styles.label}>Upload a text-based document:</label>
                <input
                  type="file"
                  style={styles.fileInput}
                  accept=".txt,.md,.csv,.json"
                  onChange={handleFileChange}
                />
                {fileName && (
                  <p style={styles.helpText}>Loaded file: {fileName}</p>
                )}
                <p style={styles.helpText}>
                  For Word / PDF, export as .txt or copy &amp; paste into the
                  Text tab.
                </p>
              </>
            )}
          </div>

          {/* === DISPLAY CONTROLS SECTION === */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Display &amp; style</div>

            <div style={styles.controlGroup}>
              <div style={styles.controlLabel}>Background theme</div>
              <select
                value={selectedThemeId}
                onChange={(e) => setSelectedThemeId(e.target.value)}
                style={styles.select}
              >
                {THEMES.map((theme) => (
                  <option key={theme.id} value={theme.id}>
                    {theme.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={styles.controlGroup}>
              <div style={styles.controlLabel}>Content style</div>
              <select
                value={layoutMode}
                onChange={(e) => setLayoutMode(e.target.value)}
                style={styles.select}
              >
                <option value="auto">Auto detect</option>
                <option value="paragraph">Paragraph</option>
                <option value="bullets">Bullets</option>
                <option value="tiles">Tiles</option>
                <option value="table">Table</option>
              </select>
              <p style={styles.helpText}>
                Auto: detects bullets, tables (with |), or tiles from your text.
              </p>
            </div>

            <div style={styles.controlGroup}>
              <div style={styles.controlLabel}>
                Font size {lockFontSize ? "(locked)" : ""}
              </div>
              <input
                type="range"
                min={24}
                max={96}
                value={fontSize}
                disabled={lockFontSize}
                onChange={(e) => setFontSize(parseInt(e.target.value, 10))}
                style={styles.range}
              />
              <div style={styles.controlValue}>{fontSize}px</div>
              <label style={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={lockFontSize}
                  onChange={(e) => setLockFontSize(e.target.checked)}
                />
                Lock font size
              </label>
            </div>

            <div style={styles.controlGroup}>
              <div style={styles.controlLabel}>Line spacing</div>
              <input
                type="range"
                min={1.1}
                max={1.8}
                step={0.05}
                value={lineHeight}
                onChange={(e) => setLineHeight(parseFloat(e.target.value))}
                style={styles.range}
              />
              <div style={styles.controlValue}>{lineHeight.toFixed(2)}</div>
            </div>

            <div style={styles.toggleRow}>
  <button
    style={{
      ...styles.toggleButton,
      ...(textAlign === "left" ? styles.toggleButtonActive : {}),
    }}
    onClick={() => setTextAlign("left")}
  >
    Left
  </button>

  <button
    style={{
      ...styles.toggleButton,
      ...(textAlign === "center" ? styles.toggleButtonActive : {}),
    }}
    onClick={() => setTextAlign("center")}
  >
    Center
  </button>

  <button
    style={{
      ...styles.toggleButton,
      ...(verticalAlign === "top" ? styles.toggleButtonActive : {}),
    }}
    onClick={() => setVerticalAlign("top")}
  >
    Top
  </button>
</div>

            
            <div style={styles.controlGroup}>
              <div style={styles.controlLabel}>Visual options</div>
              <label style={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={showSlideNumbers}
                  onChange={(e) => setShowSlideNumbers(e.target.checked)}
                />
                Show slide number
              </label>
              <label style={styles.checkboxRow}>
                <input
                  type="checkbox"
                  checked={fadeTransition}
                  onChange={(e) => setFadeTransition(e.target.checked)}
                />
                Fade transition
              </label>
            </div>
          </div>

          {/* === TIMER SECTION === */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Timer (config)</div>
            <div style={styles.controlGroup}>
              <div style={styles.controlLabel}>
                Duration: {formatTime(timerDuration)}
              </div>
              <input
                type="range"
                min={10}
                max={600}
                step={5}
                value={timerDuration}
                onChange={(e) =>
                  setTimerDuration(parseInt(e.target.value, 10))
                }
                style={styles.range}
              />
            </div>
          </div>

          {/* === SCOREBOARD SECTION (config) === */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Scoreboard (teams)</div>
            {teams.map((team) => (
              <div key={team.id} style={styles.teamRow}>
                <input
                  style={styles.teamNameInput}
                  value={team.name}
                  onChange={(e) =>
                    updateTeamName(team.id, e.target.value)
                  }
                />
                <div style={styles.teamScore}>{team.score}</div>
              </div>
            ))}
            <div style={styles.teamButtonsRow}>
              <button style={styles.secondaryButton} onClick={addTeam}>
                + Add team
              </button>
              <button style={styles.secondaryButton} onClick={removeTeam}>
                − Remove team
              </button>
              <button style={styles.secondaryButton} onClick={resetScores}>
                Reset all scores
              </button>
            </div>
          </div>

          {/* === GAME STATE SECTION === */}
          <div style={styles.section}>
            <div style={styles.sectionTitle}>Game state</div>
            <button
              style={styles.secondaryButton}
              onClick={togglePlayedForCurrent}
            >
              {playedMap[currentIndex]
                ? "Mark current game as not played"
                : "Mark current game as played"}
            </button>
            <p style={styles.helpText}>
              Played games are highlighted in the index and reports.
            </p>
          </div>
        </div>
      </div>

      {/* Main slide area (full) */}
      <div
        style={{
          ...styles.mainArea,
          background: currentTheme.background,
          color: currentTheme.color,
        }}
      >
        {/* Top bar */}
        <div style={styles.topBar}>
          {/* <div style={styles.leftStatus}>
            {paragraphs.length > 0
              ? `${paragraphs.length} game${
                  paragraphs.length === 1 ? "" : "s"
                } loaded`
              : "No games loaded yet"}
          </div> */}
          {/* <div style={styles.centerStatus}>
            {showSlideNumbers && paragraphs.length > 0 && (
              <span>
                Slide {currentIndex + 1} / {paragraphs.length}
              </span>
            )}
          </div> */}
          {/* <div style={styles.rightStatus}>
            <span style={styles.keyHint}>←</span>
            <span style={{ margin: "0 4px" }}>•</span>
            <span style={styles.keyHint}>→</span>
          </div> */}
        </div>

        {/* Slide + big timer */}
        <div style={styles.slideWrapper}>
         {/* <div style={styles.timerOverlay} title="Per-game timer">
            {formatTime(remainingTime)}
          </div>  */}
          <div
            className={fadeTransition ? "slide-content" : undefined}
            key={currentIndex}
            style={{
              ...styles.slideContent,
              fontSize: `${fontSize}px`,
              lineHeight,
              textAlign:
                effectiveLayoutMode === "paragraph" ? textAlign : "left",
            }}
          >
            {renderSlideContent()}
          </div>
        </div>
      </div>

      {/* Bottom scoreboard panel */}
      <div
        style={{
          ...styles.bottomPanel,
          transform: isBottomOpen
            ? "translateY(0)"
            : "translateY(calc(100% - 28px))",
        }}
      >
        <div
          style={styles.bottomPanelHeader}
          onClick={() => setIsBottomOpen((open) => !open)}
        >
          <span style={styles.bottomPanelTitle}>Control Panel</span>
          <span style={styles.bottomPanelSummary}>
            Game {paragraphs.length ? currentIndex + 1 : 0}/{paragraphs.length}{" "}
            • Timer {formatTime(remainingTime)}
          </span>
          <span style={styles.bottomPanelToggleIcon}>
            {isBottomOpen ? "▼" : "▲"}
          </span>
        </div>
        <div style={styles.bottomPanelBody}>
          {/* Teams + reports */}
          <div style={styles.scoreboardRow}>
            <div style={styles.scoreboardTeams}>
              {teams.map((team) => (
                <div key={team.id} style={styles.scoreCard}>
                  <input
                    style={styles.scoreCardName}
                    value={team.name}
                    onChange={(e) =>
                      updateTeamName(team.id, e.target.value)
                    }
                  />
                  <div style={styles.scoreCardValue}>{team.score}</div>
                  <div style={styles.scoreCardButtons}>
                    <button
                      style={styles.scoreMiniButton}
                      onClick={() => adjustTeamScore(team.id, -1)}
                    >
                      −
                    </button>
                    <button
                      style={styles.scoreMiniButton}
                      onClick={() => adjustTeamScore(team.id, 1)}
                    >
                      +
                    </button>
                  </div>
                </div>
              ))}
              <div style={styles.scoreboardTeamControls}>
                <button style={styles.smallButton} onClick={addTeam}>
                  +
                </button>
                <button style={styles.smallButton} onClick={removeTeam}>
                  −
                </button>
                <button style={styles.smallButton} onClick={resetScores}>
                  R
                </button>
              </div>
            </div>
            <div style={styles.reportBox}>
              <div style={styles.reportTitle}>Game stats</div>
              <div style={styles.reportLine}>
                Games played: {playedCount} / {totalGames} (
                {completionPercent}%)
              </div>
              <div style={styles.reportLine}>
                Current game:{" "}
                {paragraphs.length ? currentIndex + 1 : 0}
              </div>
              <div style={styles.reportSubTitle}>Team scores</div>
              <div style={styles.reportBars}>
                {sortedTeams.map((team) => {
                  const ratio =
                    maxScore > 0 ? team.score / maxScore : 0;
                  return (
                    <div key={team.id} style={styles.reportBarRow}>
                      <span style={styles.reportBarLabel}>
                        {team.name}
                      </span>
                      <div style={styles.reportBarTrack}>
                        <div
                          style={{
                            ...styles.reportBarFill,
                            width: `${ratio * 100}%`,
                          }}
                        />
                      </div>
                      <span style={styles.reportBarValue}>
                        {team.score}
                      </span>
                    </div>
                  );
                })}
                {sortedTeams.length === 0 && (
                  <div style={styles.reportLine}>No teams yet.</div>
                )}
              </div>
            </div>
          </div>

          {/* Navigation + timer controls */}
          <div style={styles.navRow}>
            <div style={styles.navButtonsGroup}>
              <button
                style={{
                  ...styles.navButton,
                  opacity: currentIndex === 0 ? 0.4 : 1,
                }}
                onClick={goPrev}
                disabled={
                  currentIndex === 0 || paragraphs.length === 0
                }
              >
                ◀ Previous
              </button>
              <button
                style={{
                  ...styles.navButton,
                  opacity:
                    paragraphs.length === 0 ||
                    currentIndex === paragraphs.length - 1
                      ? 0.4
                      : 1,
                }}
                onClick={goNext}
                disabled={
                  paragraphs.length === 0 ||
                  currentIndex === paragraphs.length - 1
                }
              >
                Next ▶
              </button>
              <button
                style={styles.navButton}
                onClick={shuffleGames}
                disabled={paragraphs.length <= 1}
              >
                Shuffle games
              </button>
            </div>
            <div style={styles.navTimerGroup}>
              <div style={styles.navTimerLabel}>
                Timer: {formatTime(remainingTime)}
              </div>
              <div style={styles.timerButtons}>
                <button
                  style={styles.timerButton}
                  onClick={handleTimerStartPause}
                >
                  {isTimerRunning ? "Pause" : "Start"}
                </button>
                <button
                  style={styles.timerButton}
                  onClick={handleTimerReset}
                >
                  Reset
                </button>
              </div>
            </div>
          </div>

          {/* Game picker + search */}
          <div style={styles.pickerRow}>
            <input
              style={styles.textInput}
              placeholder="Search game..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div style={styles.gameChipsRow}>
              {filteredIndices.map(({ index, text }) => {
                const label = text
                  .split(/\r?\n/)[0]
                  ?.slice(0, 40) || `Game ${index + 1}`;
                const isCurrent = index === currentIndex;
                const isPlayed = !!playedMap[index];
                return (
                  <button
                    key={index}
                    style={{
                      ...styles.gameChip,
                      ...(isCurrent ? styles.gameChipActive : {}),
                    }}
                    onClick={() => handleJumpToIndex(index)}
                  >
                    <span>
                      {index + 1}. {label}
                    </span>
                    {isPlayed && (
                      <span style={styles.gameChipPlayed}>●</span>
                    )}
                  </button>
                );
              })}
              {filteredIndices.length === 0 && (
                <span style={styles.emptyIndex}>
                  No games match your search.
                </span>
              )}
            </div>
          </div>

          {/* Config JSON controls */}
          <div style={styles.configRow}>
            <button
              style={styles.smallButton}
              onClick={handleDownloadConfig}
            >
              Download
            </button>
            <label style={styles.configFileLabel}>
              Load config:
              <input
                type="file"
                accept=".json"
                onChange={handleConfigFileChange}
                style={styles.configFileInput}
              />
            </label>
            <button
              style={styles.smallButton}
              onClick={() =>
                setShowConfigJson((show) => !show)
              }
            >
              {showConfigJson ? "Hide JSON" : "Show JSON"}
            </button>
          </div>
          {showConfigJson && (
            <textarea
              style={styles.configJsonTextarea}
              readOnly
              value={configJson}
            />
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  appRoot: {
    fontFamily: "system-ui, -apple-system, BlinkMacSystemFont, sans-serif",
    height: "100vh",
    width: "100vw",
    overflow: "hidden", // no page scroll
    background: "#000000",
    color: "#ffffff",
    position: "relative",
  },
  mainArea: {
    position: "absolute",
    left: 0,
    top: HEADER_HEIGHT,
    right: 0,
    bottom: 0,
    display: "flex",
    flexDirection: "column",
  },

  topBar: {
    height: 40,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 16px",
    fontSize: 13,
    opacity: 0.85,
  },
  leftStatus: {
    minWidth: 140,
  },
  centerStatus: {
    textAlign: "center",
    flex: 1,
  },
  rightStatus: {
    minWidth: 80,
    textAlign: "right",
    fontSize: 12,
  },
  keyHint: {
    display: "inline-block",
    padding: "2px 6px",
    border: "1px solid rgba(255,255,255,0.4)",
    borderRadius: 4,
    fontSize: 10,
  },
slideWrapper: {
  flex: 1,
  display: "flex",
  alignItems: "top",        // just a default
  justifyContent: "center",
  padding: "24px 24px 16px 24px",
  position: "relative",
  overflow: "hidden",
},

  
  slideContent: {
    maxWidth: "100%", // use full width if needed
    maxHeight: "100%", // never exceed the wrapper height
    overflow: "hidden", // clip instead of scroll
    wordWrap: "break-word",
    whiteSpace: "pre-wrap",
    fontWeight: 500,
  },
  timerOverlay: {
    position: "absolute",
    top: 10,
    right: 20,
    fontSize: 20,
    fontWeight: 600,
    padding: "4px 10px",
    borderRadius: 999,
    background: "rgba(0,0,0,0.5)",
    border: "1px solid rgba(255,255,255,0.4)",
  },

  /* Right panel & controls */
  sidePanel: {
    position: "fixed",
    top: HEADER_HEIGHT,
    right: 0,
    bottom: 0,
    width: 380,
    background: "#111111",
    color: "#f0f0f0",
    boxShadow: "0 0 16px rgba(0,0,0,0.6)",
    zIndex: 10,
    transition: "transform 0.25s ease",
    display: "flex",
    flexDirection: "column",
  },

  sidePanelHeader: {
    padding: "8px 12px",
    borderBottom: "1px solid #333",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    fontWeight: 600,
    fontSize: 14,
  },
  sidePanelBody: {
    padding: 12,
    overflowY: "auto", // scroll ONLY inside the panel, if needed
    fontSize: 13,
  },
  panelToggleButton: {
    position: "fixed",
    top: HEADER_HEIGHT + 8, // a bit below the app header
    right: 12,
    zIndex: 20,
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.5)",
    background: "rgba(0,0,0,0.8)",
    color: "#fff",
    padding: "6px 12px",
    fontSize: 13,
    cursor: "pointer",
  },

  section: {
    marginBottom: 16,
    borderBottom: "1px solid #222",
    paddingBottom: 10,
  },
  sectionTitleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    cursor: "pointer",
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: 700,
    marginBottom: 6,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    color: "#e5e5e5",
  },
  sectionToggleIcon: {
    fontSize: 12,
    opacity: 0.7,
  },

  tabRow: {
    display: "flex",
    marginBottom: 8,
    gap: 4,
  },
  tabButton: {
    flex: 1,
    border: "1px solid #444",
    background: "#181818",
    color: "#ddd",
    padding: "6px 4px",
    fontSize: 12,
    cursor: "pointer",
    borderRadius: 4,
  },
  tabButtonActive: {
    background: "#2a2a2a",
    borderColor: "#888",
    fontWeight: 600,
  },
  label: {
    display: "block",
    marginBottom: 4,
    marginTop: 6,
    fontSize: 12,
    color: "#ccc",
  },
  textarea: {
    width: "100%",
    minHeight: 120,
    resize: "vertical",
    background: "#000",
    color: "#f5f5f5",
    border: "1px solid #444",
    padding: 8,
    borderRadius: 4,
    fontSize: 13,
    fontFamily: "monospace",
    boxSizing: "border-box",
  },
  textInput: {
    width: "100%",
    background: "#000",
    color: "#f5f5f5",
    border: "1px solid #444",
    padding: 6,
    borderRadius: 4,
    fontSize: 13,
    boxSizing: "border-box",
  },
  fileInput: {
    width: "100%",
    fontSize: 12,
  },
  primaryButton: {
    marginTop: 8,
    width: "100%",
    background: "#3b82f6",
    border: "none",
    color: "#fff",
    borderRadius: 4,
    padding: "8px 10px",
    fontSize: 13,
    cursor: "pointer",
    fontWeight: 600,
  },
  secondaryButton: {
    marginTop: 8,
    width: "100%",
    background: "#222",
    border: "1px solid #555",
    color: "#fff",
    borderRadius: 4,
    padding: "6px 10px",
    fontSize: 12,
    cursor: "pointer",
  },
  helpText: {
    marginTop: 6,
    fontSize: 11,
    color: "#aaaaaa",
  },
  controlGroup: {
    marginBottom: 10,
  },
  controlLabel: {
    fontSize: 12,
    marginBottom: 4,
    color: "#f5f5f5",
    fontWeight: 600,
  },
  select: {
    width: "100%",
    padding: 6,
    borderRadius: 4,
    border: "1px solid #444",
    background: "#000",
    color: "#f5f5f5",
    fontSize: 12,
  },
  range: {
    width: "100%",
  },
  controlValue: {
    fontSize: 11,
    marginTop: 2,
    color: "#aaa",
  },
  checkboxRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    fontSize: 12,
    marginTop: 4,
  },
  toggleRow: {
    display: "flex",
    gap: 4,
  },
  toggleButton: {
    flex: 1,
    padding: "4px 8px",
    fontSize: 12,
    borderRadius: 4,
    border: "1px solid #444",
    background: "#181818",
    color: "#ddd",
    cursor: "pointer",
  },
  toggleButtonActive: {
    background: "#3b82f6",
    borderColor: "#60a5fa",
    color: "#fff",
    fontWeight: 600,
  },

  // Index styles
  indexSummary: {
    fontSize: 11,
    marginTop: 4,
    color: "#aaa",
  },
  indexList: {
    marginTop: 6,
    maxHeight: 180,
    overflowY: "auto",
  },
  indexItem: {
    width: "100%",
    textAlign: "left",
    background: "#181818",
    border: "1px solid #333",
    borderRadius: 6,
    padding: "6px 8px",
    marginBottom: 4,
    cursor: "pointer",
    fontSize: 12,
  },
  indexItemActive: {
    borderColor: "#60a5fa",
    background: "#1f2933",
  },
  indexItemHeader: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: 2,
  },
  indexItemText: {
    opacity: 0.8,
  },
  playedDot: {
    fontSize: 10,
    color: "#4ade80",
  },
  emptyIndex: {
    fontSize: 11,
    color: "#888",
    marginTop: 4,
  },

  // Timer small row in side panel
  timerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginTop: 6,
    gap: 8,
  },
  timerDisplay: {
    fontFamily: "monospace",
    fontSize: 20,
    fontWeight: 700,
  },
  timerButtons: {
    display: "flex",
    gap: 6,
  },
  timerButton: {
    padding: "4px 10px",
    borderRadius: 999,
    border: "1px solid #555",
    background: "#222",
    color: "#fff",
    fontSize: 12,
    cursor: "pointer",
  },

  // Scoreboard (side panel)
  teamRow: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    marginBottom: 4,
  },
  teamNameInput: {
    flex: 1,
    background: "#000",
    color: "#fff",
    border: "1px solid #444",
    borderRadius: 4,
    padding: "4px 6px",
    fontSize: 12,
  },
  teamScore: {
    width: 32,
    textAlign: "center",
    fontWeight: 600,
  },
  teamButtonsRow: {
    display: "flex",
    gap: 8,
    marginTop: 6,
  },

  // Content styles
  bulletList: {
    margin: 0,
    paddingLeft: "1.4em",
    fontSize: "0.9em",
  },
  bulletItem: {
    marginBottom: "0.35em",
  },
  tilesGrid: {
    display: "flex",
    flexWrap: "wrap",
    gap: 16,
    justifyContent: "center",
  },
  tile: {
    minWidth: 160,
    maxWidth: 260,
    borderRadius: 12,
    padding: "12px 16px",
    background: "rgba(0,0,0,0.35)",
    border: "1px solid rgba(255,255,255,0.2)",
    boxShadow: "0 8px 22px rgba(0,0,0,0.5)",
    fontSize: "0.9em",
    textAlign: "center",
  },
  table: {
    borderCollapse: "collapse",
    fontSize: "0.85em",
    background: "rgba(0,0,0,0.4)",
    borderRadius: 12,
    overflow: "hidden",
    minWidth: "50%",
  },
  tableHeaderCell: {
    border: "1px solid rgba(255,255,255,0.3)",
    padding: "8px 10px",
    fontWeight: 700,
    background: "rgba(255,255,255,0.06)",
  },
  tableCell: {
    border: "1px solid rgba(255,255,255,0.2)",
    padding: "6px 10px",
  },

  /* Bottom scoreboard panel */
  bottomPanel: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    height: 400,
    background: "#050505",
    borderTop: "1px solid #333",
    boxShadow: "0 -4px 16px rgba(0,0,0,0.6)",
    zIndex: 9,
    transition: "transform 0.25s ease",
    display: "flex",
    flexDirection: "column",
  },
  bottomPanelHeader: {
    height: 28,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 12px",
    fontSize: 12,
    cursor: "pointer",
    background: "#0b0b0b",
  },
  bottomPanelTitle: {
    fontWeight: 600,
  },
  bottomPanelSummary: {
    opacity: 0.8,
  },
  bottomPanelToggleIcon: {
    fontSize: 12,
    opacity: 0.8,
  },
  bottomPanelBody: {
    flex: 1,
    padding: "6px 10px 8px",
    display: "flex",
    flexDirection: "column",
    gap: 6,
    overflow: "hidden",
  },

  scoreboardRow: {
    display: "flex",
    gap: 12,
    flex: 1,
    minHeight: 0,
  },
  scoreboardTeams: {
    flex: 2,
    display: "flex",
    alignItems: "stretch",
    gap: 6,
    overflowX: "auto",
  },
  scoreCard: {
    minWidth: 120,
    maxWidth: 160,
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.3)",
    background: "rgba(0,0,0,0.7)",
    padding: "6px 8px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
  },
  scoreCardName: {
    background: "transparent",
    border: "none",
    textAlign: "center",
    borderBottom: "1px solid rgba(255,255,255,0.3)",
    color: "#fff",
    fontSize: 20,
    marginBottom: 4,
  },
  scoreCardValue: {
    fontSize: 40,
    fontWeight: 700,
    textAlign: "center",
    marginBottom: 4,
  },
  scoreCardButtons: {
    display: "flex",
    justifyContent: "space-between",
    gap: 4,
    fontSize: 40,
  },
  scoreMiniButton: {
    flex: 1,
    padding: "2px 0",
    borderRadius: 999,
    border: "1px solid #555",
    background: "#222",
    color: "#fff",
    fontSize: 20,
    cursor: "pointer",
  },
  scoreboardTeamControls: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: 4,
    paddingLeft: 4,
    fontSize: 40,
  },
  smallButton: {
    padding: "3px 8px",
    borderRadius: 999,
    border: "1px solid #555",
    background: "#222",
    color: "#fff",
    fontSize: 14,
    cursor: "pointer",
  },

  reportBox: {
    flex: 2,
    borderRadius: 10,
    border: "1px solid rgba(255,255,255,0.3)",
    background: "rgba(0,0,0,0.7)",
    padding: "6px 8px",
    fontSize: 14,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  reportTitle: {
    fontWeight: 700,
    marginBottom: 2,
  },
  reportSubTitle: {
    marginTop: 4,
    fontWeight: 600,
  },
  reportLine: {
    opacity: 0.9,
  },
  reportBars: {
    marginTop: 2,
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  reportBarRow: {
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  reportBarLabel: {
    minWidth: 70,
    fontSize: 10,
  },
  reportBarTrack: {
    flex: 1,
    height: 8,
    borderRadius: 999,
    background: "rgba(255,255,255,0.1)",
    overflow: "hidden",
  },
  reportBarFill: {
    height: "100%",
    background: "rgba(96,165,250,0.9)",
  },
  reportBarValue: {
    minWidth: 20,
    textAlign: "right",
  },

  navRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 8,
    marginTop: 2,
  },
  navButtonsGroup: {
    display: "flex",
    gap: 8,
  },
  navButton: {
    minWidth: 110,
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid rgba(255,255,255,0.45)",
    background: "rgba(0,0,0,0.35)",
    color: "#fff",
    fontSize: 12,
    cursor: "pointer",
  },
  navTimerGroup: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  navTimerLabel: {
    fontFamily: "monospace",
    fontSize: 40,
    fontWeight: 600,
  },

  pickerRow: {
    marginTop: 4,
  },
  gameChipsRow: {
    marginTop: 4,
    display: "flex",
    gap: 6,
    overflowX: "auto",
    paddingBottom: 2,
  },
  gameChip: {
    borderRadius: 999,
    border: "1px solid #444",
    background: "#111",
    color: "#fff",
    padding: "3px 8px",
    fontSize: 11,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 4,
    whiteSpace: "nowrap",
  },
  gameChipActive: {
    borderColor: "#60a5fa",
    background: "#1f2933",
  },
  gameChipPlayed: {
    fontSize: 9,
    color: "#4ade80",
  },

  configRow: {
    marginTop: 4,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  configFileLabel: {
    fontSize: 11,
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  configFileInput: {
    fontSize: 11,
  },
  configJsonTextarea: {
    marginTop: 4,
    width: "100%",
    height: 68,
    fontSize: 10,
    fontFamily: "monospace",
    background: "#000",
    color: "#0f0",
    borderRadius: 4,
    border: "1px solid #444",
    padding: 4,
    boxSizing: "border-box",
  },
};
